import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { useMaybeFetchServiceFee } from '../../hooks/useMaybeFetchServiceFee';
import selectors from '../../store/selectors';
import styles from '../Layout/DonateLayout/DonateLayout.module.scss';

import DonationAmount from './DonationAmount';
import DonationPurposesSelect from './DonationPurposesSelect';

const DonationForm = ({ accountId, csrf, getRegulations, donationPurposes }) => {
  const donationAmountOption = useSelector(selectors.selectDonationAmountOption);
  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);
  const isServiceFeeSelectedCoveredDefault = useSelector(
    selectors.selectIsServiceFeeCoveredDefault,
  );
  const isServiceFeeCoveredRequired = useSelector(selectors.selectIsServiceFeeCoveredRequired);

  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [errors, setErrors] = useState([]);

  const setError = (name, errorObject) => {
    setErrors(prevErrors => {
      const newErrors = [...prevErrors];
      newErrors[name] = errorObject;
      return newErrors;
    });
  };
  const clearErrors = () => setErrors([]);

  const [donationAmount, setDonationAmount] = useState(donationAmountOption.predefinedValues[1]);
  const [totalAmountToPay, setTotalAmountToPay] = useState(donationAmount);
  const [userCoversServiceFee, setUserCoversServiceFee] = useState(
    isServiceFeeEnabled && (isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault),
  );

  const { loading: serviceFeeLoading } = useMaybeFetchServiceFee({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault:
      isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault,
    offeringAmount: donationAmount,
    setTotalAmountToPay: value => setTotalAmountToPay(value),
    userCoversServiceFee,
  });

  return (
    <form autoComplete="on" method="post" name="transaction_form" id="transaction_form">
      <h3 className="h4">Wpisz kwotę, którą chcesz wesprzeć</h3>

      {accountId === '940339608144932867' && (
        <p className={classNames('grey-text', styles.annotation)}>
          Jeśli przekazujesz ofiarę na kolędę, w polu „Imię i nazwisko” umieść dopisek „ - kolęda”,
          np. ”Jan Kowalski - kolęda”
        </p>
      )}

      <DonationAmount
        allowFreeOfferings={false}
        clearErrors={clearErrors}
        donationAmount={donationAmount}
        errors={errors}
        isServiceFeeCoveredRequired={isServiceFeeCoveredRequired}
        isServiceFeeEnabled={isServiceFeeEnabled}
        massOfferingAmount={donationAmountOption}
        name="transaction-donation-amount"
        serviceFeeLoading={serviceFeeLoading}
        setDonationAmount={setDonationAmount}
        setError={setError}
        setUserCoversServiceFee={setUserCoversServiceFee}
        totalAmountToPay={totalAmountToPay}
        userCoversServiceFee={userCoversServiceFee}
      />

      {(donationPurposes?.length ?? 0) > 1 && (
        <DonationPurposesSelect donationPurposes={donationPurposes} />
      )}

      <div className="md-form input-group mt-3">
        <input
          className="form-control"
          id="transaction_form_comments"
          name="transaction_form[comments]"
          type="text"
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="transaction_form_comments">Komentarz do darowizny (opcjonalnie)</label>
      </div>

      <div className="md-form input-group mt-3 mb-2">
        <input
          className="form-control"
          id="transaction_form_email"
          name="transaction_form[email]"
          required="required"
          type="email"
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="transaction_form_email">E-mail zamawiającego</label>
      </div>

      <div className="md-form input-group">
        <input
          id="transaction_form_show_optional_fields"
          className="form-check-input"
          type="checkbox"
          onChange={() => setShowOptionalFields(prev => !prev)}
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="transaction_form_show_optional_fields">Chcę podać dodatkowe dane</label>
      </div>

      {showOptionalFields && (
        <>
          <div className="md-form input-group mt-3">
            <input
              className="form-control"
              id="transaction_form_name"
              name="transaction_form[name]"
              type="text"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="transaction_form_name">
              Imię i nazwisko zamawiającego (opcjonalnie)
            </label>
          </div>
          <div className="md-form input-group mt-3">
            <input
              className="form-control"
              id="transaction_form_phone"
              name="transaction_form[phone]"
              type="text"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="transaction_form_phone">Numer telefonu (opcjonalnie)</label>
          </div>

          <div className="md-form input-group mt-3">
            <input
              className="form-control"
              id="transaction_form_street"
              name="transaction_form[street]"
              type="text"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="transaction_form_street">Ulica zamawiającego (opcjonalnie)</label>
          </div>

          <div className="md-form input-group mt-3">
            <input
              className="form-control"
              id="transaction_form_zip_code"
              name="transaction_form[zipCode]"
              type="text"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="transaction_form_zip_code">Kod pocztowy (opcjonalnie)</label>
          </div>

          <div className="md-form input-group mt-3">
            <input
              className="form-control"
              id="transaction_form_city"
              name="transaction_form[city]"
              type="text"
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="transaction_form_city">Miejscowość zamawiającego (opcjonalnie)</label>
          </div>
        </>
      )}

      <div className="md-form input-group mt-0">
        <input
          className="form-check-input"
          id="transaction_form_agreement"
          name="transaction_form[agreement]"
          required="required"
          type="checkbox"
          value="1"
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="form-check-label w-100" htmlFor="transaction_form_agreement">
          Akceptuję <a href={getRegulations}>Regulamin</a>
        </label>
      </div>

      <div className="md-form input-group mt-5">
        <p className="grey-text">
          <small>
            <em>
              Po kliknięciu przycisku „Wpłacam” zostaniesz przekierowany do operatora płatności
              Tpay, gdzie sfinalizujesz wpłatę.
            </em>
          </small>
        </p>
        <button
          className="btn btn-outline-info btn-rounded btn-block z-depth-0 mt-0 mb-4 waves-effect submit-btn"
          disabled={errors['transaction-donation-amount'] || serviceFeeLoading}
          id="transaction_form_save"
          name="transaction_form[save]"
          type="submit"
        >
          Wpłacam
        </button>
      </div>

      <input name="transaction_form[_token]" type="hidden" value={csrf} />
    </form>
  );
};

DonationForm.propTypes = {
  accountId: PropTypes.string.isRequired,
  csrf: PropTypes.string.isRequired,
  getRegulations: PropTypes.string.isRequired,
  donationPurposes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      pageDescription: PropTypes.string,
    }),
  ),
};

export default DonationForm;
